import React, { useEffect, useState, useRef } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff, Save, FileDown } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from './DashboardLayout';
import TipTapEditor from '../components/TipTapEditor';
import DropboxFilePicker from '../components/DropboxFilePicker';

function formatDate(d) {
  if (!d) return '';
  const parts = d.substring(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d;
}

// ===== ADMIN: News Editor =====
function NewsEditor({ article, onSave, onCancel, apiFetch }) {
  const [form, setForm] = useState({
    title: '', short_text: '', content: '', author: '', date: new Date().toISOString().split('T')[0], published: true
  });
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (article) setForm({
      title: article.title || '', short_text: article.short_text || '',
      content: article.content || '', author: article.author || '',
      date: (article.created_at || '').substring(0, 10) || new Date().toISOString().split('T')[0],
      published: article.published !== false
    });
  }, [article]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const url = article ? `/api/news/${article.id}` : '/api/news';
    const method = article ? 'PUT' : 'POST';
    await apiFetch(url, { method, body: JSON.stringify(form) });
    setSaving(false);
    onSave();
  };

  const insertDocument = (file) => {
    setShowPicker(false);
    const editor = editorRef.current;
    if (!editor) return;
    const ext = file.name.split('.').pop()?.toUpperCase() || '';
    // Encode dropbox path in href so TipTap doesn't strip it
    const href = `#dropbox:${encodeURIComponent(file.path)}`;
    const html = `<a class="doc-download-btn" href="${href}">${ext ? ext + ' ' : ''}${file.name}</a>`;
    editor.chain().focus().insertContent(html + '&nbsp;').run();
  };

  return (
    <div data-testid="news-editor">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onCancel} data-testid="news-editor-back">
          <ArrowLeft size={14} /> Zurück
        </button>
        <h2 style={{ margin: 0, fontSize: 20 }}>{article ? 'News bearbeiten' : 'Neue News'}</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div className="of-card">
          <div className="of-section">Artikel</div>
          <div style={{ marginBottom: 12 }}>
            <label className="of-label">Titel</label>
            <input className="of-input" value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="Titel der Nachricht" data-testid="news-title" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="of-label">Kurztext</label>
            <textarea className="of-input" value={form.short_text} onChange={e => set('short_text', e.target.value)}
              rows={3} style={{ resize: 'vertical' }} placeholder="Kurze Zusammenfassung für die Übersicht..."
              data-testid="news-short-text" />
          </div>
        </div>
        <div className="of-card">
          <div className="of-section">Details</div>
          <div className="of-2col">
            <div style={{ marginBottom: 12 }}>
              <label className="of-label">Datum</label>
              <input className="of-input" type="date" value={form.date} onChange={e => set('date', e.target.value)} data-testid="news-date" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="of-label">Autor</label>
              <input className="of-input" value={form.author} onChange={e => set('author', e.target.value)}
                placeholder="Autor" data-testid="news-author" />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label className="of-label">Status</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <button type="button" className={`btn btn-sm ${form.published ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => set('published', !form.published)} data-testid="news-published-toggle"
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {form.published ? <><Eye size={14} /> Publiziert</> : <><EyeOff size={14} /> Entwurf</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="of-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div className="of-section" style={{ marginBottom: 0 }}>Inhalt</div>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPicker(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}
            data-testid="insert-document-btn">
            <FileDown size={14} /> Dokument einfügen
          </button>
        </div>
        <TipTapEditor content={form.content} onChange={v => set('content', v)} placeholder="Vollständigen Nachrichtentext hier eingeben..." editorRef={editorRef} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.title.trim()}
          style={{ padding: '8px 24px' }} data-testid="news-save-btn">
          <Save size={14} /> {saving ? 'Speichern...' : article ? 'Aktualisieren' : 'Erstellen'}
        </button>
        <button className="btn btn-secondary" onClick={onCancel} style={{ padding: '8px 20px' }}>Abbrechen</button>
      </div>

      <DropboxFilePicker open={showPicker} onClose={() => setShowPicker(false)}
        onSelect={insertDocument} apiFetch={apiFetch} />
    </div>
  );
}

// ===== ADMIN: News List =====
function AdminNewsList({ news, onEdit, onNew, onDelete, onPreview }) {
  return (
    <div data-testid="news-admin-list">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>News verwalten</h2>
        <button className="btn btn-primary" onClick={onNew} data-testid="news-new-btn" style={{ fontSize: 13 }}>
          <Plus size={14} /> Neue News
        </button>
      </div>

      {news.length === 0 ? (
        <div className="of-card" style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
          <p style={{ margin: 0, fontSize: 13 }}>Noch keine News vorhanden</p>
        </div>
      ) : (
        <div className="of-card" style={{ padding: 0 }}>
          <div className="table-wrap" style={{ borderTop: 'none' }}>
            <table data-testid="news-table">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Datum</th>
                  <th>Titel</th>
                  <th>Autor</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 120 }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {news.map(n => (
                  <tr key={n.id} data-testid={`news-row-${n.id}`} onClick={() => onEdit(n)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{formatDate(n.created_at)}</td>
                    <td style={{ fontWeight: 500 }}>{n.title}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{n.author}</td>
                    <td>
                      {n.published ? (
                        <span className="badge badge-success">Publiziert</span>
                      ) : (
                        <span className="badge badge-warning">Entwurf</span>
                      )}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-icon" title="Vorschau" onClick={() => onPreview(n)} data-testid={`news-preview-${n.id}`}
                          style={{ color: 'var(--primary)' }}><Eye size={14} /></button>
                        <button className="btn-icon edit" title="Bearbeiten" onClick={() => onEdit(n)} data-testid={`news-edit-${n.id}`}><Pencil size={14} /></button>
                        <button className="btn-icon" title="Löschen" onClick={() => onDelete(n.id)} data-testid={`news-delete-${n.id}`}
                          style={{ color: '#EF4444' }}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== PROVIDER: News Detail =====
function NewsDetail({ article, onBack, apiFetch }) {
  const handleContentClick = async (e) => {
    const btn = e.target.closest('.doc-download-btn');
    if (!btn) return;
    e.preventDefault();
    const href = btn.getAttribute('href') || '';
    if (!href.startsWith('#dropbox:')) return;
    const path = decodeURIComponent(href.slice('#dropbox:'.length));
    if (!path) return;
    btn.style.opacity = '0.6';
    btn.textContent = 'Laden...';
    try {
      const r = await apiFetch(`/api/downloads/file/link?path=${encodeURIComponent(path)}`);
      const d = await r?.json();
      if (d?.url) window.open(d.url, '_blank');
      else alert('Download nicht verfügbar');
    } catch { alert('Fehler beim Download'); }
    // Restore button text
    const ext = path.split('/').pop()?.split('.').pop()?.toUpperCase() || '';
    const name = path.split('/').pop() || 'Dokument';
    btn.textContent = `${ext ? ext + ' ' : ''}${name}`;
    btn.style.opacity = '1';
  };

  return (
    <div data-testid="news-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={onBack} data-testid="news-detail-back">
          <ArrowLeft size={14} /> Zurück
        </button>
      </div>
      <div className="of-card" style={{ padding: '32px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 4, height: 36, background: 'var(--primary)', borderRadius: 2 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {formatDate(article.created_at)}
            </div>
            {article.author && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{article.author}</div>}
          </div>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 24px', lineHeight: 1.3, color: 'var(--text-primary)' }}>{article.title}</h1>
        <div style={{ width: 60, height: 3, background: 'var(--primary)', borderRadius: 2, marginBottom: 24 }} />
        <div className="news-content" dangerouslySetInnerHTML={{ __html: article.content }}
          onClick={handleContentClick}
          style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--text-primary)' }} />
      </div>
      <style>{`
        .news-content h1 { font-size: 24px; font-weight: 700; margin: 24px 0 12px; }
        .news-content h2 { font-size: 20px; font-weight: 700; margin: 20px 0 10px; }
        .news-content h3 { font-size: 17px; font-weight: 600; margin: 16px 0 8px; }
        .news-content p { margin: 0 0 12px; }
        .news-content a { color: var(--primary); text-decoration: underline; }
        .news-content ul, .news-content ol { padding-left: 24px; margin: 10px 0; }
        .news-content ul { list-style-type: disc; }
        .news-content ol { list-style-type: decimal; }
        .news-content li { margin: 4px 0; }
        .news-content strong { font-weight: 700; }
        .news-content .doc-download-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px; border-radius: 6px; font-size: 14px; font-weight: 600;
          background: var(--primary, #CC0000); color: #fff !important;
          text-decoration: none !important; cursor: pointer; margin: 4px 2px;
          transition: opacity 0.15s, transform 0.15s; line-height: 1.4;
        }
        .news-content .doc-download-btn:hover { opacity: 0.85; transform: translateY(-1px); }
      `}</style>
    </div>
  );
}

// ===== PROVIDER: News List (Read-only) =====
function ProviderNewsList({ news, onSelect }) {
  return (
    <div data-testid="news-provider-list">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Aktuelle Informationen</h2>
      </div>

      {news.length === 0 ? (
        <div className="of-card" style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
          <p style={{ margin: 0, fontSize: 13 }}>Keine aktuellen Nachrichten vorhanden</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {news.map(n => (
            <div key={n.id} className="of-card" onClick={() => onSelect(n)} data-testid={`news-item-${n.id}`}
              style={{ cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s', padding: '16px 20px' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 48, textAlign: 'center', paddingTop: 2 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: 'var(--primary)' }}>
                    {n.created_at?.substring(8, 10)}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 2 }}>
                    {['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][parseInt(n.created_at?.substring(5, 7) || '1') - 1]}
                  </div>
                </div>
                <div style={{ width: 3, background: 'var(--primary)', borderRadius: 2, alignSelf: 'stretch', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', marginBottom: 4 }}>{n.title}</div>
                  {n.short_text && (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      {n.short_text.length > 180 ? n.short_text.substring(0, 180) + '...' : n.short_text}
                    </div>
                  )}
                  {n.author && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{n.author}</div>
                  )}
                </div>
                <div style={{ flexShrink: 0, color: 'var(--text-muted)', alignSelf: 'center' }}>
                  <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== MAIN COMPONENT =====
export default function NewsPage({ userType }) {
  const { apiFetch } = useApi();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | edit | detail
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = userType <= 2;

  const loadNews = async () => {
    setLoading(true);
    const r = await apiFetch('/api/news');
    const d = await r?.json();
    if (Array.isArray(d)) {
      setNews(d);
      const articleId = searchParams.get('article');
      if (articleId && !selected) {
        const found = d.find(n => String(n.id) === articleId);
        if (found) { setSelected(found); setView('detail'); }
      }
    }
    setLoading(false);
  };

  useEffect(() => { loadNews(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('News wirklich löschen?')) return;
    await apiFetch(`/api/news/${id}`, { method: 'DELETE' });
    loadNews();
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>;

  // Detail view (Preview / Provider read view)
  if (view === 'detail' && selected) {
    return <NewsDetail article={selected} onBack={() => { setView('list'); setSelected(null); setSearchParams({}); }} apiFetch={apiFetch} />;
  }

  // Editor view (Admin)
  if (view === 'edit') {
    return <NewsEditor article={selected} apiFetch={apiFetch}
      onSave={() => { setView('list'); setSelected(null); loadNews(); }}
      onCancel={() => { setView('list'); setSelected(null); }} />;
  }

  // List view
  if (isAdmin) {
    return <AdminNewsList news={news}
      onEdit={n => { setSelected(n); setView('edit'); }}
      onNew={() => { setSelected(null); setView('edit'); }}
      onPreview={n => { setSelected(n); setView('detail'); }}
      onDelete={handleDelete} />;
  }

  return <ProviderNewsList news={news} onSelect={n => { setSelected(n); setView('detail'); }} />;
}
